import React, { useState, useEffect, useCallback } from "react";
import { Database, Upload, ArrowRight, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import ProvenanceBadges from "@/components/provenance/ProvenanceBadges";
import { useAuth } from "@/auth/AuthContext";
import { buildDemoMauritiusMarketsPayload, demoMauritiusRouteFor, DEMO_MAURITIUS_MARKET_KEYS, isMauritiusDemoOrg } from "@/lib/demoMauritius";
import { scoreLabel, scoreClasses, fmtCompact } from "@/lib/marketDisplay";
import { localizeLabel, marketRowMatchesFocus } from "@/lib/marketFormat";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const LodgeTag = () => <span className="ml-1 rounded-full bg-[#EEF2FF] px-1.5 py-0.5 text-[10px] font-semibold not-italic text-indigo-600" title="Source : Lodge Data (benchmark)">Lodge</span>;

const SourceTag = ({ source }) => {
  if (source === "DEMO") return <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold not-italic text-amber-700" title="Valeur simulée dans la démonstration">Démo</span>;
  if (source === "LODGE") return <span className="ml-1 rounded-full bg-[#ECF3EC] px-1.5 py-0.5 text-[10px] font-semibold not-italic text-[#2E7D46]" title="Source : Lodge Data">Lodge Data</span>;
  if (source === "CLIENT") return <span className="ml-1 rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold not-italic text-blue-700" title="Source : Client Data">Client Data</span>;
  return null;
};

const NA = () => <span className="text-xs italic text-slate-400" title="Donnée non disponible">Non disponible</span>;

const Sparkline = ({ data }) => {
  if (!data || !data.some((v) => v)) return <NA />;
  const w = 88, h = 24, max = Math.max(...data), min = Math.min(...data), range = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${(h - ((v - min) / range) * h).toFixed(1)}`).join(" ");
  return <svg width={w} height={h}><polyline points={pts} fill="none" stroke="#3E7256" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
};

const Evolution = ({ v }) => (v == null ? <NA /> : (
  <span className={v >= 0 ? "font-semibold text-emerald-600" : "font-semibold text-rose-600"}>
    {v >= 0 ? "+" : ""}{v}%
  </span>
));

const ScoreBadge = ({ score }) => {
  if (score == null) return <span className="text-xs italic text-slate-400" title="Score non disponible">Non disponible</span>;
  return (
    <span data-testid="lodge-score-badge" className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold ${scoreClasses(score)}`}>
      {score}<span className="font-medium opacity-70">· {scoreLabel(score)}</span>
    </span>
  );
};

const FilterSelect = ({ label, value, onChange, options, allLabel, testid }) => (
  <div className="min-w-[170px]">
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-10 text-sm" data-testid={testid}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__all__">{allLabel}</SelectItem>
        {options.map((o) => <SelectItem key={o.value ?? o} value={o.value ?? o} data-testid={`${testid}-${o.value ?? o}`}>{o.label ?? o}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const SORT_OPTIONS = [
  { value: "lodge_score", label: "Opportunity Score" },
  { value: "volume", label: "Volume visiteurs / nuitées" },
  { value: "growth", label: "Croissance" },
  { value: "air", label: "Accessibilité aérienne" },
  { value: "spend", label: "Dépense" },
  { value: "length_of_stay", label: "Durée de séjour" },
];

// Phase 3B — national Benchmark block. Visually distinct, clearly labeled, never mixed with the
// org's own (PRIMARY) markets table above. Rendered only for CITY/REGION orgs with national data.
const BenchmarkSection = ({ benchmark }) => {
  if (!benchmark || !benchmark.has_benchmark || !(benchmark.markets || []).length) return null;
  const scope = benchmark.benchmark_scope || {};
  const rows = benchmark.markets || [];
  return (
    <section className="rounded-2xl border border-indigo-100 bg-indigo-50/40 p-5" data-testid="benchmark-section">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <span data-testid="benchmark-badge" className="rounded-full bg-indigo-600 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
          Benchmark
        </span>
        <h2 className="font-heading text-lg font-bold text-[#1E3D2B]">Benchmark national</h2>
        <span className="text-sm text-indigo-700" data-testid="benchmark-scope">
          Référence : {scope.entity || "national"}
        </span>
      </div>
      <p className="mb-4 max-w-3xl text-xs text-slate-500">
        Données <span className="font-semibold text-slate-600">nationales de référence</span>, fournies à titre de comparaison.
        Elles sont <span className="font-semibold text-slate-600">distinctes de votre territoire principal</span> et ne sont jamais comptabilisées comme vos données propres.
      </p>
      <div className="overflow-hidden rounded-xl border border-indigo-100 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-indigo-100 bg-indigo-50/60 text-left text-xs font-semibold uppercase tracking-wider text-indigo-700">
                <th className="px-5 py-3">Marché</th>
                <th className="px-5 py-3">Visiteurs / Nuitées</th>
                <th className="px-5 py-3">Évolution</th>
                <th className="px-5 py-3">Dépense</th>
                <th className="px-5 py-3">Durée de séjour</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.key} data-testid={`benchmark-row-${m.key}`} className="border-b border-indigo-50 last:border-0">
                  <td className="px-5 py-3.5 text-sm font-semibold text-[#1E3D2B]">{m.name}</td>
                  <td className="px-5 py-3.5 font-mono text-sm text-slate-700">
                    {m.volume == null ? <NA /> : <span>{fmtCompact(m.volume)}{m.volume_metric && <span className="ml-1 block font-sans text-[10px] text-slate-400">{m.volume_metric}</span>}</span>}
                  </td>
                  <td className="px-5 py-3.5 text-sm"><Evolution v={m.evolution} /></td>
                  <td className="px-5 py-3.5 font-mono text-sm text-slate-700">{m.spend == null ? <NA /> : fmtCompact(m.spend)}</td>
                  <td className="px-5 py-3.5 text-sm text-slate-700">{m.length_of_stay == null ? <NA /> : `${m.length_of_stay} j`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default function Markets() {
  const { activeOrgId, activeMembership } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [benchmark, setBenchmark] = useState(null);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ destination: "__all__", period: "__all__", source_market: "__all__", sort_by: "lodge_score" });

  const load = useCallback(async () => {
    if (!activeOrgId) return;
    setLoading(true);
    try {
      const params = {};
      if (f.period !== "__all__") params.period = f.period;
      if (f.source_market !== "__all__") params.source_market = f.source_market;
      if (f.destination !== "__all__") params.destination = f.destination;
      if (f.sort_by) params.sort_by = f.sort_by;
      const { data } = await api.get(`/organizations/${activeOrgId}/markets-data`, { params });
      setData(data);
    } catch {
      setData({ has_data: false, filters: { data_types: [], source_markets: [], periods: [], destinations: [] }, markets: [] });
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, f]);

  // Phase 3B — national Benchmark (CITY/REGION orgs only). Isolated call; never mixed with markets.
  const loadBenchmark = useCallback(async () => {
    if (!activeOrgId) return;
    try {
      const { data } = await api.get(`/organizations/${activeOrgId}/benchmark-data`);
      setBenchmark(data);
    } catch {
      setBenchmark(null);
    }
  }, [activeOrgId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadBenchmark(); }, [loadBenchmark]);

  const org = activeMembership?.org || {};
  // Demo Maurice only: 4 approved routes, real ADI air passengers from the API,
  // every other metric explicitly unavailable (no illustrative value, no enrichment).
  const mauritiusDemo = isMauritiusDemoOrg(org);
  const [demoAir, setDemoAir] = useState({ orgId: null, byKey: {} });
  useEffect(() => {
    if (!mauritiusDemo || !activeOrgId) return undefined;
    let cancelled = false;
    const headers = { "X-Organization-ID": activeOrgId };
    Promise.all(DEMO_MAURITIUS_MARKET_KEYS.map((key) => api.get(`/market-data/air/${key}`, { headers })
      .then(({ data: air }) => [key, air?.kpis?.airPassengers?.raw ?? null])
      .catch(() => [key, null])))
      .then((pairs) => { if (!cancelled) setDemoAir({ orgId: activeOrgId, byKey: Object.fromEntries(pairs) }); });
    return () => { cancelled = true; };
  }, [activeOrgId, mauritiusDemo]);
  const displayedData = mauritiusDemo
    ? ((!data?.has_data || !(data?.markets || []).length)
      ? buildDemoMauritiusMarketsPayload(demoAir.orgId === activeOrgId ? demoAir.byKey : {})
      : { ...data, markets: (data.markets || []).filter((row) => demoMauritiusRouteFor(row?.iso || row?.key)) })
    : data;
  // Demo Maurice only: real API data on the page (Lodge Data air or served tourism rows) ->
  // same contextual label as the Dashboard instead of the synthetic-data badge.
  const mauritiusLiveData = mauritiusDemo && (displayedData?.markets || []).some((row) =>
    (row?.air_passengers != null && row?.sources?.air === "LODGE") || row?.volume != null);
  const filters = displayedData?.filters || { source_markets: [], periods: [], destinations: [] };
  const markets = displayedData?.markets || [];
  const illustrativeFallback = false;
  const focusKey = new URLSearchParams(location.search).get("market");
  const [focusOrigin, focusDestination] = String(focusKey || "").split("-");
  const focusLabel = focusKey && focusDestination ? localizeLabel(focusOrigin, focusDestination) : focusKey;
  const shownMarkets = focusKey ? markets.filter((row) => marketRowMatchesFocus(row, focusKey)) : markets;
  const clearFocus = () => navigate("/markets", { replace: true });

  return (
    <div className="space-y-8" data-testid="markets-page">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-[#1E3D2B] sm:text-3xl">Markets</h1>
          {mauritiusLiveData ? (
            <span data-testid="markets-mixed-provenance" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Données réelles · démo identifiée par bloc
            </span>
          ) : <ProvenanceBadges meta={displayedData} />}
        </div>
        <p className="mt-2 max-w-2xl text-sm text-slate-500">Analysez et comparez les marchés touristiques de votre destination.</p>
        <p className="mt-1 max-w-2xl text-xs text-slate-400">Tourisme, aérien et signaux de marché réunis dans une même analyse.</p>
      </div>

      <div className="flex flex-wrap items-end gap-3" data-testid="markets-filters">
        <FilterSelect label="Destination" value={f.destination} onChange={(v) => setF({ ...f, destination: v })} options={filters.destinations} allLabel="Toutes destinations" testid="filter-destination" />
        <FilterSelect label="Période" value={f.period} onChange={(v) => setF({ ...f, period: v })} options={filters.periods} allLabel="Toutes périodes" testid="filter-period" />
        <FilterSelect label="Marché" value={f.source_market} onChange={(v) => setF({ ...f, source_market: v })} options={filters.source_markets} allLabel="Tous marchés" testid="filter-source-market" />
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs font-medium text-slate-400">Trier par</span>
          <FilterSelect label="Trier par" value={f.sort_by} onChange={(v) => setF({ ...f, sort_by: v })} options={SORT_OPTIONS} allLabel="Opportunity Score" testid="filter-sort-by" />
        </div>
      </div>

      {focusKey && (
        <div className="flex items-center gap-2" data-testid="markets-focus">
          <span className="text-xs font-medium text-slate-500">Marché sélectionné</span>
          <span data-testid="markets-focus-chip" className="inline-flex items-center gap-1.5 rounded-full border border-[#BBD8C5] bg-[#F1F7F3] px-3 py-1 text-xs font-semibold text-[#1E3D2B]">
            {focusLabel}
            <button type="button" data-testid="markets-focus-clear" onClick={clearFocus} aria-label="Afficher tous les marchés" className="rounded-full p-0.5 hover:bg-white"><X size={12} /></button>
          </span>
        </div>
      )}

      {!loading && displayedData?.data_unavailable && (
        <div data-testid="markets-unavailable-notice" className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs leading-relaxed text-slate-600">
          <strong>Démonstration Maurice.</strong> Seuls les passagers aériens Lodge Market Data (Sabre ADI) sont disponibles pour France, Inde, Japon et Émirats arabes unis → Maurice. Les indicateurs touristiques sont « non disponibles » : aucune valeur n’est simulée.
        </div>
      )}

      {!loading && (illustrativeFallback || displayedData?.demo_enriched_fields) && (
        <div data-testid="markets-demo-notice" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-900">
          <strong>Simulation illustrative.</strong> Les valeurs marquées « Démo » complètent uniquement les champs absents. Elles ne remplacent jamais les données observées Lodge ou Client.
        </div>
      )}

      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-12" data-testid="markets-loading" aria-live="polite">
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-48 rounded bg-slate-200" />
            <div className="h-12 rounded bg-slate-100" />
            <div className="h-12 rounded bg-slate-100" />
            <div className="h-12 rounded bg-slate-100" />
          </div>
          <span className="sr-only">Chargement des marchés…</span>
        </div>
      ) : (!displayedData?.has_data || markets.length === 0) ? (
        <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center" data-testid="markets-empty">
          <Database size={36} className="mx-auto mb-3 text-slate-300" />
          <p className="text-sm font-medium text-slate-600">Aucune donnée de marché disponible</p>
          <p className="mx-auto mt-1 max-w-sm text-sm text-slate-400">Importez des données touristiques dans le Data Hub pour enrichir l'analyse des marchés.</p>
          <Link to="/data-hub" data-testid="markets-import-cta" className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#1E3D2B] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#2C5540]">
            <Upload size={15} /> Aller au Data Hub
          </Link>
        </div>
      ) : focusKey && shownMarkets.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white py-12 text-center" data-testid="markets-focus-empty">
          <p className="text-sm font-medium text-slate-600">Aucune donnée disponible pour {focusLabel}</p>
          <button type="button" data-testid="markets-focus-empty-clear" onClick={clearFocus} className="mt-3 text-sm font-semibold text-[#3E7256] hover:underline">Afficher tous les marchés</button>
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="markets-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-3">Marché</th>
                    <th className="px-5 py-3">Visiteurs / Nuitées</th>
                    <th className="px-5 py-3">Évolution</th>
                    <th className="px-5 py-3">Part destination</th>
                    <th className="px-5 py-3">Aérien</th>
                    <th className="px-5 py-3">Dépense</th>
                    <th className="px-5 py-3">Durée de séjour</th>
                    <th className="px-5 py-3">Lodge Score</th>
                  </tr>
                </thead>
                <tbody>
                  {shownMarkets.map((m) => (
                    <tr key={m.key} data-testid={`market-row-${m.key}`} onClick={() => navigate(`/markets/${m.air_market_key || m.key}`)}
                      className="group cursor-pointer border-b border-slate-100 transition-colors last:border-0 hover:bg-[#F5F8F5]">
                      <td className="px-5 py-4 text-sm font-semibold text-[#1E3D2B]">
                        <span className="inline-flex items-center gap-2">{m.name}<ArrowRight size={13} className="opacity-0 transition-opacity group-hover:opacity-60" /></span>
                      </td>
                      <td className="px-5 py-4 font-mono text-sm text-slate-700">
                        {m.volume == null ? <NA /> : (
                          <span>{fmtCompact(m.volume)}{m.sources?.volume === "LODGE" && <LodgeTag />}
                            {m.volume_metric && <span className="ml-1 block font-sans text-[10px] not-italic text-slate-400">{m.volume_metric}</span>}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-sm"><Evolution v={m.evolution} /></td>
                      <td className="px-5 py-4 text-sm text-slate-700">{m.part_destination == null ? <NA /> : `${m.part_destination}%`}</td>
                      <td className="px-5 py-4 text-sm text-slate-700">
                        {m.air_passengers == null ? <NA /> : (
                          <span className="inline-flex items-center gap-1.5 font-mono">{fmtCompact(m.air_passengers)}
                            <SourceTag source={m.sources?.air || "LODGE"} />
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4 font-mono text-sm text-slate-700">{m.spend == null ? <NA /> : <span>{fmtCompact(m.spend)}<SourceTag source={m.sources?.spend} /></span>}</td>
                      <td className="px-5 py-4 text-sm text-slate-700">{m.length_of_stay == null ? <NA /> : <span>{m.length_of_stay} j{m.length_of_stay_origin === "derived" && <span className="ml-1 text-[10px] text-slate-400">(calculé)</span>}<SourceTag source={m.sources?.length_of_stay} /></span>}</td>
                      <td className="px-5 py-4"><ScoreBadge score={m.lodge_score} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-xs text-slate-400">
            Sources : <span className="font-medium text-slate-500">Client Data</span> (données importées) · <span className="font-medium text-slate-500">Lodge Data</span> (aérien). L'aérien n'est affiché que lorsqu'un flux entrant vers votre destination est disponible.
          </p>
        </>
      )}

      <BenchmarkSection benchmark={benchmark} />
    </div>
  );
}
